import { useId, useMemo, useState } from 'react';
import { ChevronDown, Plus, Tags } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { EmptyState, ErrorState, PageHeader, StatusPill } from '@/components/app';
import type { Plan } from '../../api/payments';
import { usePlans } from './hooks/usePlans';
import { PlansTable, PlansTableSkeleton, PlanDialog } from './components';
import { byDuration } from './planFormat';
import { VARIANT_STATUS_LABELS, variantStatusTone } from '../Experiment/variantStatus';
import { labelOf } from '@/lib/labels';

function plural(n: number, one: string, few: string, many: string) {
  const d10 = n % 10;
  const d100 = n % 100;
  if (d10 === 1 && d100 !== 11) return `${n} ${one}`;
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

export default function PlansPage() {
  const {
    plans,
    variants,
    isLoading,
    loadError,
    retryLoad,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPlan,
    formData,
    isSubmitting,
    togglingId,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  } = usePlans();
  const ids = useId().replace(/:/g, '');
  const [variantsOpen, setVariantsOpen] = useState(false);

  const basePlans = useMemo(() => plans.filter((p) => !p.variant_id).sort(byDuration), [plans]);

  // Тарифы ценовых вариантов, сгруппированные по варианту (порядок — как в списке вариантов).
  const variantGroups = useMemo(() => {
    const byVariant = new Map<string, Plan[]>();
    for (const p of plans) {
      if (!p.variant_id) continue;
      const list = byVariant.get(p.variant_id) ?? [];
      list.push(p);
      byVariant.set(p.variant_id, list);
    }
    const known = variants
      .filter((v) => byVariant.has(v.id))
      .map((v) => ({ id: v.id, name: v.name, status: v.status as string }));
    const unknown = [...byVariant.keys()]
      .filter((id) => !variants.some((v) => v.id === id))
      .map((id) => {
        const v = byVariant.get(id)![0].variant;
        return { id, name: v?.name ?? 'Без названия', status: v?.status ?? '' };
      });
    return [...known, ...unknown].map((g) => ({ ...g, plans: byVariant.get(g.id)!.sort(byDuration) }));
  }, [plans, variants]);

  const variantPlanCount = variantGroups.reduce((n, g) => n + g.plans.length, 0);
  const activeBase = basePlans.filter((p) => p.is_active).length;
  const selectedId = isEditDialogOpen ? editingPlan?.id ?? null : null;

  const tableProps = {
    onEdit: handleEdit,
    onDelete: handleDelete,
    onToggleActive: handleToggleActive,
    togglingId,
    selectedId,
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Тарифы"
        subtitle="Планы на экране оплаты. Цены в сумах, цена в день считается сама"
        actions={
          <>
            <span className="flex items-center gap-2">
              <Switch
                id={`${ids}-inactive`}
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
              <label htmlFor={`${ids}-inactive`} className="text-sm text-text-2">
                Показать неактивные
              </label>
            </span>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus />
              Создать тариф
            </Button>
          </>
        }
      />

      <Card className="overflow-hidden" role="region" aria-labelledby={`${ids}-base`}>
        <div className="flex flex-col gap-0.5 px-[18px] py-4">
          <b id={`${ids}-base`} className="font-display text-[17px] font-bold">
            Базовый прайс-лист
          </b>
          <span className="text-[13px] text-text-2">
            Видят все, у кого нет Назначения варианта
            {!isLoading && !loadError && ` · ${plural(activeBase, 'активный план', 'активных плана', 'активных планов')}`}
          </span>
        </div>
        {loadError ? (
          <ErrorState message={loadError} onRetry={retryLoad} className="border-t border-border-soft" />
        ) : isLoading ? (
          <PlansTableSkeleton />
        ) : basePlans.length === 0 ? (
          <EmptyState
            className="border-t border-border-soft"
            icon={<Tags />}
            title="В базовом прайс-листе нет тарифов"
            description={includeInactive ? 'Пользователи без варианта не увидят планов на экране оплаты.' : 'Возможно, тарифы неактивны — включите «Показать неактивные».'}
          />
        ) : (
          <PlansTable plans={basePlans} {...tableProps} />
        )}
      </Card>

      {!loadError && !isLoading && variantGroups.length > 0 && (
        <Collapsible open={variantsOpen} onOpenChange={setVariantsOpen} asChild>
          <Card className="overflow-hidden" role="region" aria-label="Ценовые варианты">
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-[18px] py-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
              <span className="flex flex-col gap-0.5">
                <b className="font-display text-[17px] font-bold">Ценовые варианты</b>
                <span className="text-[13px] text-text-2">
                  {plural(variantGroups.length, 'вариант', 'варианта', 'вариантов')} ·{' '}
                  {plural(variantPlanCount, 'план', 'плана', 'планов')}. Их видят только пользователи с Назначением на
                  вариант
                </span>
              </span>
              <ChevronDown
                aria-hidden
                className={cn('size-[18px] shrink-0 transition-transform', variantsOpen && 'rotate-180')}
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              {variantGroups.map((g) => (
                <section key={g.id} aria-label={`Вариант «${g.name}»`} className="border-t border-border-soft">
                  <div className="flex flex-wrap items-center gap-2.5 px-[18px] pb-1 pt-4">
                    <b className="font-display text-[15px] font-bold">{g.name}</b>
                    {g.status && (
                      <StatusPill size="sm" tone={variantStatusTone(g.status)}>
                        {labelOf(VARIANT_STATUS_LABELS, g.status)}
                      </StatusPill>
                    )}
                  </div>
                  <PlansTable plans={g.plans} {...tableProps} />
                </section>
              ))}
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <PlanDialog
        plans={plans}
        editingPlanId={null}
        variants={variants}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        title="Новый тариф"
        description="План появится на экране оплаты, как только вы его активируете"
        submitLabel="Создать тариф"
      />

      <PlanDialog
        plans={plans}
        editingPlanId={editingPlan?.id ?? null}
        variants={variants}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        title="Редактировать тариф"
        description="Изменения применятся сразу после сохранения"
        submitLabel="Сохранить"
      />
    </div>
  );
}
